import { useContext } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import DynamicPhoneNumber from "common/blocks/DynamicPhoneNumber";
import Info from "common/blocks/Info";
import RichText from "common/blocks/RichText";
import UIIcon from "common/blocks/UIIcon";
import useFinanceDataContext from "features/vehicleDetails/providers/financeData/useFinanceDataContext";
import { ResolvedHigherOfferSidePanel } from "resolvers/data/higherOfferSidePanelResolver";
import type { ResolvedPopup } from "resolvers/data/popupResolver";
import type { ResolvedRichText } from "resolvers/data/richTextResolver";
import type { PartExchange } from "services/motorpoint/partExchange";
import formatCurrency from "utils/formatCurrency";

import { FinanceCalculatorContext } from "../context";
import styles from "./CalculatorStats.module.scss";
import PartExchangeDetails from "./PartExchangeDetails";

const cx = classNames.bind(styles);

export type CalculatorStatsProps = {
    enquiryLink: string;
    partExchange?: PartExchange;
    isPxMoreThanMaxDeposit?: boolean;
    hasPX?: boolean;
    settings: {
        calculationFailedMessage: ResolvedRichText;
        partExchangeEquityMoreThanMaxDepositMessage: ResolvedRichText;
        whatIsPXPopup: ResolvedPopup;
        higherOfferLabel: string;
        higherOfferButtonText: string;
        higherOfferSidePanel: ResolvedHigherOfferSidePanel;
    };
};

const monthlyPaymentLabel = "Monthly Payment";
const finalPaymentLabels = ["Optional Final Payment", "Final Payment"];
export const representativeAPRLabel = "Representative APR";

const EXCLUDED_FROM_LIST: string[] = [monthlyPaymentLabel, ...finalPaymentLabels];

const CalculatorStats = ({
    partExchange,
    hasPX,
    enquiryLink,
    isPxMoreThanMaxDeposit,
    ...props
}: CalculatorStatsProps) => {
    const { financeData, financeError } = useFinanceDataContext();
    const { warrantyPrice, paintProtectionPrice, exteriorProtectionPrice } =
        useContext(FinanceCalculatorContext);

    if (financeError && !isPxMoreThanMaxDeposit) {
        return (
            <div className={cx("stats")}>
                <Info>
                    <RichText {...props.settings.calculationFailedMessage} />
                </Info>
                <Button
                    href={enquiryLink}
                    target="_blank"
                    theme="secondary"
                    fullWidth
                    className={cx("button")}
                    ariaLabel="Make enquiry"
                >
                    <UIIcon icon="Mail" className={cx("mailIcon")} /> Make enquiry
                </Button>
                <div className={cx("contactDetails")}>
                    <UIIcon icon="Tel" className={cx("phoneIcon")} />
                    <DynamicPhoneNumber isLink />
                </div>
            </div>
        );
    }

    const monthlyPayment = financeData?.find(({ Label }) => Label === monthlyPaymentLabel);
    const finalPayment = financeData?.find(({ Label }) => finalPaymentLabels.includes(Label));
    const filteredParams = (financeData || []).filter(
        ({ Label }) => !EXCLUDED_FROM_LIST.includes(Label)
    );

    return (
        <div className={cx("stats")}>
            {!isPxMoreThanMaxDeposit && (
                <div className={cx("paymentDetails")}>
                    <div className={cx("detail")}>
                        <span
                            className={cx("amount")}
                            dangerouslySetInnerHTML={{
                                __html: formatCurrency(monthlyPayment?.Value ?? 0, 2),
                            }}
                        />
                        <span className={cx("description")}>{monthlyPayment?.Label}</span>
                    </div>
                    <div className={cx("detail")}>
                        <span
                            className={cx("amount", "small")}
                            dangerouslySetInnerHTML={{
                                __html: formatCurrency(finalPayment?.Value ?? 0, 2),
                            }}
                        />
                        <span className={cx("description")}>{finalPayment?.Label}</span>
                    </div>
                </div>
            )}
            {hasPX && (
                <PartExchangeDetails
                    enquiryLink={enquiryLink}
                    partExchange={partExchange}
                    isPxMoreThanMaxDeposit={isPxMoreThanMaxDeposit}
                    partExchangeEquityMoreThanMaxDepositMessage={
                        props.settings.partExchangeEquityMoreThanMaxDepositMessage
                    }
                    whatIsPXPopup={props.settings.whatIsPXPopup}
                    higherOfferLabel={props.settings.higherOfferLabel}
                    higherOfferButtonText={props.settings.higherOfferButtonText}
                    higherOfferSidePanel={props.settings.higherOfferSidePanel}
                />
            )}

            {!isPxMoreThanMaxDeposit && (
                <ul className={cx("overview")}>
                    {filteredParams.map((item) => (
                        <li key={item.Label} className={cx("item")}>
                            <span>{item.Label}</span>
                            <span className={cx("value")}>{item.FormattedValue}</span>
                        </li>
                    ))}
                    {warrantyPrice && (
                        <li key="warranty" className={cx("item")}>
                            <span>Motorpoint Extended Warranty</span>
                            <span className={cx("value")}>{warrantyPrice}</span>
                        </li>
                    )}
                    {paintProtectionPrice && (
                        <li key="paintProtection" className={cx("item")}>
                            <span>Williams Paint Protection</span>
                            <span className={cx("value")}>{paintProtectionPrice}</span>
                        </li>
                    )}
                    {exteriorProtectionPrice && (
                        <li key="exteriorProtection" className={cx("item")}>
                            <span>Cosmetic and Alloy Wheel Maintenance Plan</span>
                            <span className={cx("value")}>{exteriorProtectionPrice}</span>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
};

export default CalculatorStats;
